<template>
  <div>
    <article>
      <job-preview-company-introduction
        v-if="!isLightPlan"
        :text-count-mode="textCountMode"
        :highlight-mode="highlightMode"
        :comment-highlights="getAnyCommentHighlights(targetIdMap.companyInfo.intro.prefix)"
        :text-counts="textCounts.intro"
        :catch-copy="companyInfo.catchCopy"
        :text="companyInfo.introText"
        :image="companyInfo.introImage"
        v-on="$listeners"
      />

      <job-preview-content-outline
        title="会社名"
        :value="companyInfo.name"
        data-test="name"
        v-on="$listeners"
      />

      <job-preview-content-outline
        title="事業内容"
        :comment-highlights="getCommentHighlights(targetIdMap.companyInfo.businessContent)"
        :text-count-mode="textCountMode"
        :highlight-mode="highlightMode"
        :text-counts="[getTextCount('事業内容', companyInfo.businessContent, targetIdMap.companyInfo.businessContent)]"
        :target-id="targetIdMap.companyInfo.businessContent"
        :value="companyInfo.businessContent"
        data-test="businessContent"
        v-on="$listeners"
      />

      <job-preview-content-outline
        v-if="companyInfo.established"
        title="設立"
        :comment-highlights="getCommentHighlights(targetIdMap.companyInfo.established)"
        :text-count-mode="textCountMode"
        :highlight-mode="highlightMode"
        :text-counts="[getTextCount('設立', companyInfo.established, targetIdMap.companyInfo.established)]"
        :target-id="targetIdMap.companyInfo.established"
        :value="companyInfo.established"
        data-test="established"
        v-on="$listeners"
      />

      <job-preview-content-outline
        v-if="companyInfo.capital"
        title="資本金"
        :comment-highlights="getCommentHighlights(targetIdMap.companyInfo.capital)"
        :text-count-mode="textCountMode"
        :highlight-mode="highlightMode"
        :text-counts="[getTextCount('資本金', companyInfo.capital, targetIdMap.companyInfo.capital)]"
        :target-id="targetIdMap.companyInfo.capital"
        :value="companyInfo.capital"
        data-test="capital"
        v-on="$listeners"
      />

      <job-preview-content-outline
        v-if="companyInfo.sale"
        title="売上高"
        :comment-highlights="getCommentHighlights(targetIdMap.companyInfo.sale)"
        :text-count-mode="textCountMode"
        :highlight-mode="highlightMode"
        :text-counts="[getTextCount('売上高', companyInfo.sale, targetIdMap.companyInfo.sale)]"
        :target-id="targetIdMap.companyInfo.sale"
        :value="companyInfo.sale"
        data-test="sale"
        v-on="$listeners"
      />

      <job-preview-content-outline
        v-if="isShow.employee"
        title="従業員数"
        :comment-highlights="getCommentHighlights(targetIdMap.companyInfo.employeeNumber)"
        :text-count-mode="textCountMode"
        :highlight-mode="highlightMode"
        :text-counts="[getTextCount('従業員数', companyInfo.employeeNumber, targetIdMap.companyInfo.employeeNumber)]"
        :target-id="targetIdMap.companyInfo.employeeNumber"
        :value="companyInfo.employeeNumber"
        data-test="employeeNumber"
        v-on="$listeners"
      >
        <template
          v-if="companyInfo.averageAge || companyInfo.midCareerPercentage"
          #sub
        >
          <job-preview-content-sub
            v-if="companyInfo.averageAge"
            title="平均年齢"
            :comment-highlights="getCommentHighlights(targetIdMap.companyInfo.averageAge)"
            :text-count-mode="textCountMode"
            :highlight-mode="highlightMode"
            :text-counts="[getTextCount('平均年齢', companyInfo.averageAge, targetIdMap.companyInfo.averageAge)]"
            :target-id="targetIdMap.companyInfo.averageAge"
            :value="companyInfo.averageAge"
            data-test="averageAge"
            v-on="$listeners"
          />
          <job-preview-content-sub
            v-if="companyInfo.midCareerPercentage"
            title="中途入社者の割合"
            :comment-highlights="getCommentHighlights(targetIdMap.companyInfo.midCareerPercentage)"
            :text-count-mode="textCountMode"
            :highlight-mode="highlightMode"
            :text-counts="[getTextCount('中途入社者の割合', companyInfo.midCareerPercentage, targetIdMap.companyInfo.midCareerPercentage)]"
            :target-id="targetIdMap.companyInfo.midCareerPercentage"
            :value="companyInfo.midCareerPercentage"
            data-test="midCareerPercentage"
            v-on="$listeners"
          />
        </template>
      </job-preview-content-outline>

      <job-preview-content-outline
        v-if="isShow.representative"
        title="代表者"
        :comment-highlights="getCommentHighlights(targetIdMap.companyInfo.representative)"
        :text-count-mode="textCountMode"
        :highlight-mode="highlightMode"
        :text-counts="[getTextCount('代表者', companyInfo.representative, targetIdMap.companyInfo.representative)]"
        :target-id="targetIdMap.companyInfo.representative"
        :value="companyInfo.representative"
        data-test="representative"
        v-on="$listeners"
      >
        <template
          v-if="companyInfo.representativeProfile"
          #sub
        >
          <job-preview-content-sub
            title="代表者略歴"
            :comment-highlights="getCommentHighlights(targetIdMap.companyInfo.representativeProfile)"
            :text-count-mode="textCountMode"
            :highlight-mode="highlightMode"
            :text-counts="[getTextCount('代表者略歴', companyInfo.representativeProfile, targetIdMap.companyInfo.representativeProfile)]"
            :target-id="targetIdMap.companyInfo.representativeProfile"
            :value="companyInfo.representativeProfile"
            data-test="representativeProfile"
            v-on="$listeners"
          />
        </template>
      </job-preview-content-outline>

      <job-preview-content-outline
        v-if="companyInfo.note"
        title="備考"
        :comment-highlights="getCommentHighlights(targetIdMap.companyInfo.note)"
        :text-count-mode="textCountMode"
        :highlight-mode="highlightMode"
        :text-counts="[getTextCount('備考', companyInfo.note, targetIdMap.companyInfo.note)]"
        :target-id="targetIdMap.companyInfo.note"
        :value="companyInfo.note"
        data-test="note"
        v-on="$listeners"
      />

      <job-preview-content-outline
        v-if="companyInfo.hp"
        title="企業HP"
        data-test="hp"
        v-on="$listeners"
      >
        <job-preview-content
          :comment-highlights="getCommentHighlights(targetIdMap.companyInfo.hp)"
          :highlight-mode="highlightMode"
          data-test="hp-content"
          v-on="$listeners"
        >
          <template #body>
            <p class="clearfix">
              <component
                :is="tag"
                :id="targetIdMap.companyInfo.hp"
                :href="companyInfo.hp"
                :class="{'text-underline':highlightMode}"
                rel="nofollow"
                target="blank"
                data-test="value"
                v-text="companyInfo.hp"
              />
            </p>
          </template>
        </job-preview-content>
      </job-preview-content-outline>
    </article>

    <job-preview-company-images
      v-if="!isLightPlan"
      :highlight-mode="highlightMode"
      :text-count-mode="textCountMode"
      :comment-highlights="getAnyCommentHighlights(targetIdMap.companyInfo.images.prefix)"
      :text-counts="textCounts.images()"
      :company-info="companyInfo"
      v-on="$listeners"
    />
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/preview/pc/offer/base" as *;
</style>

<script>
import JobPreviewRightColumn from '@/components/molecules/JobPreviewRightColumn'
import JobPreviewContentOutline from '@/components/atoms/JobPreviewContentOutline'
import JobPreviewContentSub from '@/components/atoms/JobPreviewContentSub'
import JobPreviewContent from '@/components/atoms/JobPreviewContent'
import jobPreviewCommon from '@/lib/jobPreviewCommon'
import jobPreviewHighlight from '@/lib/jobPreviewHighlight'
import jobPreviewUrlHighlight from '@/lib/jobPreviewUrlHighlight'
import JobPreviewCompanyIntroduction from '@/components/atoms/JobPreviewCompanyIntroduction'
import JobPreviewCompanyImages from '@/components/atoms/JobPreviewCompanyImages'

export default {
  components: {
    JobPreviewCompanyImages,
    JobPreviewCompanyIntroduction,
    JobPreviewRightColumn,
    JobPreviewContentOutline,
    JobPreviewContentSub,
    JobPreviewContent
  },
  mixins: [jobPreviewCommon, jobPreviewHighlight, jobPreviewUrlHighlight],
  props: {
    isLightPlan: {
      type: Boolean,
      default: false
    },
    companyInfo: {
      type: Object,
      required: true
    },
    commentHighlights: {
      type: Array,
      default: () => []
    },
    highlightMode: {
      type: Boolean,
      required: true
    },
    textCountMode: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    textCounts () {
      return {
        intro: [
          this.getTextCount('企業紹介キャッチ', this.companyInfo.catchCopy, this.targetIdMap.companyInfo.intro.catchCopy),
          this.getTextCount('企業紹介本文', this.companyInfo.introText, this.targetIdMap.companyInfo.intro.text)
        ],
        images: () => {
          const imagesTextCount = []
          if (this.companyInfo.companyImage1Caption)imagesTextCount.push(this.getTextCount('会社写真[1]キャプション', this.companyInfo.companyImage1Caption, this.targetIdMap.companyInfo.images.caption))
          if (this.companyInfo.companyImage2Caption)imagesTextCount.push(this.getTextCount('会社写真[2]キャプション', this.companyInfo.companyImage2Caption, this.targetIdMap.companyInfo.images.caption))
          if (this.companyInfo.companyImage3Caption)imagesTextCount.push(this.getTextCount('会社写真[3]キャプション', this.companyInfo.companyImage3Caption, this.targetIdMap.companyInfo.images.caption))
          return imagesTextCount
        }
      }
    },
    urlHighlightTargetIds () {
      return [this.targetIdMap.companyInfo.hp]
    }
  }
}
</script>
